import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // ✅ Función para extender la sesión
  const extendSession = (hoursToExtend = 24) => {
    const currentExpiry = localStorage.getItem('tokenExpiry');
    if (currentExpiry) {
      const newExpiry = new Date(Date.now() + (hoursToExtend * 60 * 60 * 1000)).toISOString();
      localStorage.setItem('tokenExpiry', newExpiry);
      console.log(`✅ Sesión extendida por ${hoursToExtend} horas`);
    }
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      console.log('🔐 Intentando login con:', credentials.email);

      const result = await authService.login(credentials);

      if (result.success) {
        const userData = result.data.user;
        const normalizedRole = normalizeRole(userData.role);

        setUser({ ...userData, role: normalizedRole });
        setUserRole(normalizedRole);
        setIsAuthenticated(true);
        setLoading(false);

        console.log('✅ Login exitoso:', userData.email || userData.username, 'Rol normalizado:', normalizedRole);

        return { success: true, user: { ...userData, role: normalizedRole } };
      } else {
        setLoading(false);
        return {
          success: false,
          error: result.message || 'Error al iniciar sesión'
        };
      }
    } catch (error) {
      setLoading(false);
      console.error('❌ Error en login:', error);
      console.error('📋 Respuesta completa del servidor:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Error al iniciar sesión'
      };
    }
  };

  // Función para normalizar roles del backend a los esperados por el frontend
  const normalizeRole = (role) => {
    if (!role) return 'Usuario';

    const roleMap = {
      'superadmin': 'SuperAdmin',
      'super_admin': 'SuperAdmin',
      'super admin': 'SuperAdmin',
      'administrador': 'Admin',
      'admin': 'Admin',
      'administrator': 'Admin',
      'usuario': 'Usuario',
      'user': 'Usuario',
      'client': 'Usuario',
      'cliente': 'Usuario'
    };

    const normalizedInput = role.toLowerCase().trim();
    const mappedRole = roleMap[normalizedInput];

    const validRoles = ['SuperAdmin', 'Admin', 'Usuario'];
    return mappedRole || (validRoles.includes(role) ? role : 'Usuario');
  };

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const refreshToken = localStorage.getItem('refreshToken');
      const userRole = localStorage.getItem('userRole');
      const userData = localStorage.getItem('userData');
      const tokenExpiry = localStorage.getItem('tokenExpiry');

      // ✅ Si no hay token, salir inmediatamente
      if (!token) {
        console.log('❌ No hay token válido, requiere login');
        setLoading(false);
        return;
      }

      // ✅ Verificar expiración del token
      if (tokenExpiry) {
        const now = new Date().getTime();
        const expiryTime = new Date(tokenExpiry).getTime();

        if (now >= expiryTime) {
          console.log('🔒 Token expirado');

          // Intentar renovar con refreshToken si está disponible
          if (refreshToken) {
            try {
              console.log('🔄 Intentando renovar token...');
              const refreshResponse = await authAPI.refresh({ refreshToken });
              const newTokenData = refreshResponse.data.data || refreshResponse.data;

              // Actualizar tokens
              const newExpiresAt = new Date(Date.now() + (newTokenData.expiresIn * 1000)).toISOString();
              localStorage.setItem('authToken', newTokenData.accessToken);
              localStorage.setItem('refreshToken', newTokenData.refreshToken);
              localStorage.setItem('tokenExpiry', newExpiresAt);

              console.log('✅ Token renovado exitosamente');
            } catch (refreshError) {
              console.log('❌ Error renovando token, requiere nuevo login');
              clearAuthData();
              setLoading(false);
              return;
            }
          } else {
            console.log('🔒 No hay refreshToken, requiere nuevo login');
            clearAuthData();
            setLoading(false);
            return;
          }
        }
      }

      // ✅ Restaurar sesión con datos guardados
      if (userData) {
        try {
          const parsedUserData = JSON.parse(userData);
          const normalizedRole = normalizeRole(parsedUserData.role || userRole);

          setUser({ ...parsedUserData, role: normalizedRole });
          setUserRole(normalizedRole);
          setIsAuthenticated(true);

          // Actualizar localStorage con rol normalizado si es necesario
          if (parsedUserData.role !== normalizedRole) {
            localStorage.setItem('userRole', normalizedRole);
            localStorage.setItem('userData', JSON.stringify({ ...parsedUserData, role: normalizedRole }));
          }

          console.log('✅ Sesión restaurada desde localStorage:', parsedUserData.email || parsedUserData.username, 'Rol:', normalizedRole);
        } catch (parseError) {
          console.error('❌ Error parseando userData, intentando desde API');
          await restoreFromAPI(userRole);
        }
      } else if (userRole) {
        // Si tenemos rol pero no datos completos, intentar desde API
        await restoreFromAPI(userRole);
      }

    } catch (error) {
      console.error('❌ Error verificando autenticación:', error);
      clearAuthData();
    } finally {
      setLoading(false);
    }
  };

  // ✅ Función helper para restaurar desde API
  const restoreFromAPI = async (fallbackRole) => {
    try {
      const response = await authAPI.getCurrentUser();
      const normalizedRole = normalizeRole(response.data.role || fallbackRole);
      const userWithNormalizedRole = { ...response.data, role: normalizedRole };

      setUser(userWithNormalizedRole);
      setUserRole(normalizedRole);
      setIsAuthenticated(true);

      // Guardar los datos del usuario para futuras recargas
      localStorage.setItem('userData', JSON.stringify(userWithNormalizedRole));
      localStorage.setItem('userRole', normalizedRole);

      console.log('✅ Sesión restaurada desde API:', response.data.email || response.data.username, 'Rol:', normalizedRole);
    } catch (apiError) {
      console.log('❌ Error obteniendo datos de la API, usando datos básicos');
      // Crear usuario básico con la info que tenemos
      const normalizedRole = normalizeRole(fallbackRole);
      const basicUser = {
        role: normalizedRole,
        email: 'usuario@sistema.com',
        username: 'Usuario'
      };
      setUser(basicUser);
      setUserRole(normalizedRole);
      setIsAuthenticated(true);
      console.log('✅ Sesión restaurada con datos básicos, Rol:', normalizedRole);
    }
  };

  // ✅ Función helper para limpiar datos de autenticación
  const clearAuthData = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenType');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userData');
    localStorage.removeItem('tokenExpiry');
    setUser(null);
    setUserRole(null);
    setIsAuthenticated(false);
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Error al registrar usuario'
      };
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await authService.logout();
      clearAuthData();
      console.log('✅ Sesión cerrada exitosamente');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Limpiar datos localmente incluso si hay error
      clearAuthData();
    } finally {
      setLoading(false);
    }
  };

  // ✅ Funciones helper mejoradas
  const isSuperAdmin = () => {
    return userRole === 'SuperAdmin' || userRole === 'Administrador';
  };

  const isAdmin = () => {
    return userRole === 'Admin' || userRole === 'SuperAdmin' || userRole === 'Administrador';
  };

  const isUser = () => {
    return userRole === 'Usuario' || userRole === 'User';
  };

  const hasRole = (role) => {
    return userRole === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(userRole);
  };

  // ✅ Función mejorada para verificar si el token está por expirar
  const isTokenExpiring = (minutesBeforeExpiry = 5) => {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (!tokenExpiry) return false;

    const now = new Date().getTime();
    const expiryTime = new Date(tokenExpiry).getTime();
    const timeUntilExpiry = expiryTime - now;
    const minutesUntilExpiry = timeUntilExpiry / (1000 * 60);

    return minutesUntilExpiry <= minutesBeforeExpiry && minutesUntilExpiry > 0;
  };

  // ✅ Función para obtener tiempo restante de sesión
  const getSessionTimeLeft = () => {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (!tokenExpiry) return 0;

    const now = new Date().getTime();
    const expiryTime = new Date(tokenExpiry).getTime();
    const timeLeft = expiryTime - now;

    return Math.max(0, timeLeft);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    userRole,
    login,
    register,
    logout,
    isSuperAdmin,
    isAdmin,
    isUser,
    hasRole,
    hasAnyRole,
    isTokenExpiring,
    extendSession,        // ✅ Nueva función
    getSessionTimeLeft,   // ✅ Nueva función
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};